import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface MOMSettingsProps {
  companyId?: string;
  settings: any[];
  refreshSettings: () => void;
}

const MOMSettings: React.FC<MOMSettingsProps> = ({ companyId, settings, refreshSettings }) => {
  const [newCategory, setNewCategory] = useState('Task Type');
  const [newValue, setNewValue] = useState('');
  const [loading, setLoading] = useState(false);

  const categories = ['Task Type', 'Team', 'Projects', 'Subprojects', 'Externalities', 'Status'];

  const handleAddSetting = async () => {
    if (!companyId || !newValue.trim()) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('mom_settings')
        .insert([{ company_id: companyId, category: newCategory, value: newValue }]);
      
      if (error) throw error;
      setNewValue('');
      refreshSettings();
    } catch (err) {
      console.error('Error adding setting:', err);
      alert('Failed to add setting');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSetting = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this setting?')) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('mom_settings')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      refreshSettings();
    } catch (err) {
      console.error('Error deleting setting:', err);
      alert('Failed to delete setting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Dynamic Dropdown Settings</h2>
      
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
        <select 
          value={newCategory} 
          onChange={e => setNewCategory(e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }}
        >
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input 
          type="text" 
          placeholder="New Value" 
          value={newValue} 
          onChange={e => setNewValue(e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)', flex: 1 }}
        />
        <button 
          onClick={handleAddSetting} 
          disabled={loading || !newValue.trim()}
          className="btn btn-primary"
        >
          {loading ? 'Adding...' : 'Add Value'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {categories.map(category => {
          const categorySettings = settings.filter(s => s.category === category);
          return (
            <div key={category} style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>{category}</h3>
              {categorySettings.length === 0 ? (
                <p style={{ color: 'var(--muted)' }}>No values added.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {categorySettings.map(setting => (
                    <li key={setting.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span>{setting.value}</span>
                      <button 
                        onClick={() => handleDeleteSetting(setting.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MOMSettings;
